import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api_url_v1 } from '../utils/Config';
import api from '../utils/Api';

// Async Thunks for Training Programs
export const fetchTrainingPrograms = createAsyncThunk(
  'trainingPrograms/fetchTrainingPrograms',
  async (_, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.get(`${api_url_v1}/training-programs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchTrainingProgramById = createAsyncThunk(
  'trainingPrograms/fetchTrainingProgramById',
  async (trainingProgramId, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.get(`${api_url_v1}/training-programs/${trainingProgramId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createTrainingProgram = createAsyncThunk(
  'trainingPrograms/createTrainingProgram',
  async (trainingProgramData, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.post(`${api_url_v1}/training-programs`, trainingProgramData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateTrainingProgram = createAsyncThunk(
  'trainingPrograms/updateTrainingProgram',
  async ({ id, ...updates }, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.put(`${api_url_v1}/training-programs/${id}`, updates, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTrainingProgram = createAsyncThunk(
  'trainingPrograms/deleteTrainingProgram',
  async (id, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      await api.delete(`${api_url_v1}/training-programs/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  items: [],
  currentProgram: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

const trainingProgramsSlice = createSlice({
  name: 'trainingPrograms',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
    setCurrentProgram: (state, action) => {
      state.currentProgram = action.payload;
    },
    clearCurrentProgram: (state) => {
      state.currentProgram = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all training programs
      .addCase(fetchTrainingPrograms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrainingPrograms.fulfilled, (state, action) => {
        state.items = action.payload;
        state.isLoading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchTrainingPrograms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch training programs';
      })

      // Fetch training program by ID
      .addCase(fetchTrainingProgramById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrainingProgramById.fulfilled, (state, action) => {
        state.currentProgram = action.payload;
        state.isLoading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchTrainingProgramById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch training program';
      })

      // Create training program
      .addCase(createTrainingProgram.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTrainingProgram.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.isLoading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createTrainingProgram.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create training program';
      })

      // Update training program
      .addCase(updateTrainingProgram.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTrainingProgram.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentProgram && state.currentProgram.id === action.payload.id) {
          state.currentProgram = action.payload;
        }
        state.isLoading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateTrainingProgram.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update training program';
      })

      // Delete training program
      .addCase(deleteTrainingProgram.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteTrainingProgram.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        if (state.currentProgram && state.currentProgram.id === action.payload) {
          state.currentProgram = null;
        }
        state.isLoading = false;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(deleteTrainingProgram.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete training program';
      });
  },
});

export const { clearErrors, resetState, setCurrentProgram, clearCurrentProgram } = trainingProgramsSlice.actions;

// Selectors
export const selectTrainingPrograms = (state) => state.trainingPrograms.items;
export const selectCurrentTrainingProgram = (state) => state.trainingPrograms.currentProgram;
export const selectTrainingProgramsLoading = (state) => state.trainingPrograms.isLoading;
export const selectTrainingProgramsError = (state) => state.trainingPrograms.error;
export const selectTrainingProgramsLastUpdated = (state) => state.trainingPrograms.lastUpdated;

export default trainingProgramsSlice.reducer;